import {
	faInfoCircle,
	faPlus,
	faTable,
	faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { Icon } from '../../../../components/base/Icon';
import { Button } from '../../../../components/forms/Button';
import { useThemeColors } from '../../../../providers/ThemeProvider';
import { useTranslation } from '../../../../providers/TranslationProvider';
import type {
	ColumnConfiguration,
	ListBuilderConfiguration,
	ListConfiguration,
} from '../../../types';
import { DraggableList, DragHandle } from '../../shared/DraggableList';
import { ColumnDetailsTab } from './details/ColumnDetailsTab';
import { FieldsTab } from './fields/FieldsTab';

interface ColumnConfigTabsProps {
	listConfig: ListConfiguration;
	configuration?: ListBuilderConfiguration | undefined;
	onUpdateList: (updates: Partial<ListConfiguration>) => void;
	availableFields: any[];
	isModalOpen?: boolean;
	isReadOnly?: boolean;
}

export function ColumnConfigTabs({
	listConfig,
	configuration,
	onUpdateList,
	availableFields,
	isModalOpen = false,
	isReadOnly = false,
}: ColumnConfigTabsProps) {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const [selectedColumnIndex, setSelectedColumnIndex] = useState<number>(0);
	const [activeTab, setActiveTab] = useState<'details' | 'fields'>('details');
	const [dragOverColumnIndex, setDragOverColumnIndex] = useState<number | null>(
		null,
	);

	const columns = listConfig.columns || [];
	const selectedColumn = columns[selectedColumnIndex];

	// Create a new column
	const addColumn = () => {
		const newColumn: ColumnConfiguration = {
			id: `column_${Date.now()}`,
			headers: [
				{ id: `header_${Date.now()}`, text: `Column ${columns.length + 1}` },
			],
			alignment: 'left',
			verticalAlignment: 'top',
			sortable: false,
			fields: [],
		};

		const updatedColumns = [...columns, newColumn];
		updateColumnsWithConfig(updatedColumns);
		setSelectedColumnIndex(updatedColumns.length - 1);
	};

	// Delete a column
	const deleteColumn = (index: number) => {
		const updatedColumns = columns.filter((_, i) => i !== index);
		updateColumnsWithConfig(updatedColumns);

		if (selectedColumnIndex >= updatedColumns.length) {
			setSelectedColumnIndex(Math.max(0, updatedColumns.length - 1));
		}
	};

	// Reorder columns
	const reorderColumns = (reorderedColumns: ColumnConfiguration[]) => {
		// Get the ID of currently selected column before reordering
		const selectedColumnId = columns[selectedColumnIndex]?.id;

		updateColumnsWithConfig(reorderedColumns);

		// Find new index of the previously selected column
		if (selectedColumnId) {
			const newIndex = reorderedColumns.findIndex(
				(column) => column.id === selectedColumnId,
			);
			if (newIndex !== -1 && newIndex !== selectedColumnIndex) {
				setSelectedColumnIndex(newIndex);
			}
		}
	};

	// Update the selected column
	const updateColumn = (updates: Partial<ColumnConfiguration>) => {
		const updatedColumns = [...columns];
		updatedColumns[selectedColumnIndex] = {
			...updatedColumns[selectedColumnIndex],
			...updates,
		};
		onUpdateList({ columns: updatedColumns });
	};

	// Helper to update columns and trigger configuration update
	const updateColumnsWithConfig = (updatedColumns: ColumnConfiguration[]) => {
		onUpdateList({ columns: updatedColumns });
	};

	// Handle field drop on a column
	const handleFieldDropOnColumn = (
		targetColumnIndex: number,
		dragData: string,
	) => {
		try {
			// Check if dragData is JSON (field drop) or just a string (column reorder)
			let parsedData;
			try {
				parsedData = JSON.parse(dragData);
			} catch (e) {
				// Not JSON, probably a column ID - ignore column drops here
				return;
			}

			// Check if this is a field from the Objects tab (no sourceColumnId)
			if (
				parsedData.fieldName &&
				!parsedData.field &&
				!parsedData.sourceColumnId
			) {
				// This is a field from the Objects tab
				// Create a new field configuration
				const newField = {
					id: `field_${Date.now()}`,
					displayStyle: 'text',
					dataConfig: {
						type: 'field',
						field: {
							fieldName: parsedData.fieldName,
							objectTable: parsedData.objectTable || 'item', // Default to item if not specified
						},
					},
					styleConfig: {
						size: 'sm',
						colorRule: { type: 'default' },
					},
				};

				// Add the field to the target column
				const updatedColumns = [...columns];
				updatedColumns[targetColumnIndex] = {
					...updatedColumns[targetColumnIndex],
					fields: [...updatedColumns[targetColumnIndex].fields, newField],
				};

				updateColumnsWithConfig(updatedColumns);

				// Update the selected column to the one that received the drop
				setSelectedColumnIndex(targetColumnIndex);
				return;
			}

			// Handle field drops between columns
			const { field, sourceColumnId } = parsedData;

			// Don't allow dropping on the same column
			if (sourceColumnId === columns[targetColumnIndex].id) {
				return;
			}

			// Add field to target column
			const updatedColumns = columns.map((col, index) => {
				if (index === targetColumnIndex) {
					// Add field to this column
					return {
						...col,
						fields: [...col.fields, field],
					};
				} else if (col.id === sourceColumnId) {
					// Remove field from source column
					return {
						...col,
						fields: col.fields.filter((f) => f.id !== field.id),
					};
				}
				return col;
			});

			updateColumnsWithConfig(updatedColumns);
		} catch (e) {
			// Error: Failed to handle field drop:
		}
	};

	const tabs = [
		{ id: 'details', label: 'Details', icon: faInfoCircle },
		{ id: 'fields', label: 'Fields', icon: faTable },
	];

	return (
		// `min-w-0` keeps this row inside its parent's width even when the
		// configurator panel's natural content (DisplayStyleSelector picker,
		// field list, etc.) is wider than the available row. Without it, the
		// row inflates to its min-content (sum of un-shrinkable children),
		// which propagates up the modal and triggers a horizontal scrollbar.
		<div className="flex flex-1 min-w-0">
			{/* Columns Sidebar */}
			<div
				className={`w-56 border-r flex flex-col min-h-0 ${colors.classes.borderDefault} ${colors.classes.bgSecondary}`}
			>
				<div className={`p-4 border-b ${colors.classes.borderDefault}`}>
					<div className="flex justify-between items-center">
						<h3
							className={`text-base font-semibold ${colors.classes.textPrimary}`}
						>
							{t('Columns')}
						</h3>
						{!isReadOnly && (
							<Button onClick={addColumn} variant="primary" size="sm">
								<Icon icon={faPlus} size="xs" className="mr-1" />
								{t('Add')}
							</Button>
						)}
					</div>
				</div>

				<div className="flex-1 min-h-0 overflow-y-auto">
					<DraggableList
						className="!min-h-0"
						data={columns}
						keyExtractor={(column) => column.id}
						dragDataExtractor={(column) => column.id}
						listId="columns-list"
						allowInternalReordering={!isReadOnly}
						allowPositionalDrops={true}
						acceptDropFrom={[]}
						onDrop={(dragData, targetIndex) => {
							// Reordering handled by onReorder
						}}
						renderItem={(column, index, isDragging) => (
							<div
								onClick={() => {
									setSelectedColumnIndex(index);
								}}
								onDragOver={(e) => {
									if (isReadOnly) return;
									e.preventDefault();
									e.stopPropagation();
									// Check if dragging a field
									const dragData = e.dataTransfer?.types.includes('text/plain');
									if (dragData) {
										// Check if it's a field by examining global drag state
										const globalDragState = (window as any).globalDragState;
										if (globalDragState?.sourceListId) {
											// Check if it's from a fields sidebar or from objects tab
											const isFromFieldsSidebar =
												globalDragState.sourceListId.startsWith(
													'fields-sidebar-',
												);
											const isFromObjectsTab =
												globalDragState.sourceListId.startsWith('available-') ||
												globalDragState.sourceListId.startsWith('active-');

											if (isFromFieldsSidebar) {
												// Check if it's not from the current column
												const sourceColumnId =
													globalDragState.sourceListId.replace(
														'fields-sidebar-',
														'',
													);
												if (sourceColumnId !== columns[index].id) {
													setDragOverColumnIndex(index);
												}
											} else if (isFromObjectsTab) {
												// Always show hover for fields from objects tab
												setDragOverColumnIndex(index);
											}
										}
									}
								}}
								onDragLeave={(e) => {
									e.stopPropagation();
									// Only clear if leaving the element entirely
									if (!e.currentTarget.contains(e.relatedTarget as Node)) {
										setDragOverColumnIndex(null);
									}
								}}
								onDrop={(e) => {
									if (isReadOnly) return;
									e.preventDefault();
									e.stopPropagation();
									const dragData = e.dataTransfer?.getData('text/plain');
									if (dragData) {
										handleFieldDropOnColumn(index, dragData);
										// Select the column that received the drop
										setSelectedColumnIndex(index);
									}
									setDragOverColumnIndex(null);
								}}
								className={`
                    flex items-center p-3 mx-2 my-1 rounded-md cursor-pointer transition-all border
                    ${
											dragOverColumnIndex === index
												? `${colors.classes.bgInfo} ${colors.classes.borderInfo}`
												: selectedColumnIndex === index
													? colors.classes.buttonGroupSelected
													: isDragging
														? `${colors.classes.bgSecondary} border-transparent`
														: `${colors.classes.bgPrimary} border-transparent ${colors.classes.bgPrimaryHover}`
										}
                  `}
							>
								{!isReadOnly && <DragHandle className="mr-1" />}
								<span
									className={`
                    flex-1 text-sm
                    ${
											selectedColumnIndex === index
												? 'font-semibold'
												: `font-medium ${colors.classes.textPrimary}`
										}
                  `}
								>
									{column.headers[0]?.text || ''}
								</span>
								{dragOverColumnIndex === index && (
									<span
										className={`text-xs ${colors.classes.textInfo} ml-2 font-medium`}
									>
										Drop here
									</span>
								)}
								{!isReadOnly && (
									<button
										onClick={(e) => {
											e.stopPropagation();
											deleteColumn(index);
										}}
										className={`ml-1 p-1 rounded ${colors.classes.bgPrimaryHover}`}
									>
										<Icon
											icon={faTrash}
											size="xs"
											className={colors.classes.textSecondary}
										/>
									</button>
								)}
							</div>
						)}
						onReorder={reorderColumns}
					/>
				</div>
			</div>

			{/* Configuration Panel with Tabs */}
			{/* `min-w-0` keeps this flex-1 child inside its share of the row.
			    Without it the panel's `min-width: auto` resolves to its
			    content's min-content (the DisplayStyleSelector + FieldList
			    interior), which can exceed the row width on narrower modals
			    and push the column-editor area past the modal's edge. */}
			<div className="flex-1 min-w-0 flex flex-col">
				{selectedColumn && (
					<>
						{/* Tab Navigation */}
						<div
							className={`flex border-b ${colors.classes.borderDefault} ${colors.classes.bgPrimary}`}
						>
							{tabs.map((tab) => (
								<button
									key={tab.id}
									onClick={() => {
										setActiveTab(tab.id as 'details' | 'fields');
									}}
									className={`
                    flex items-center px-4 py-3 border-b-2 transition-colors
                    ${
											activeTab === tab.id
												? `${colors.classes.borderPrimary} ${colors.classes.textPrimary}`
												: `border-transparent ${colors.classes.textSecondary} ${colors.classes.textPrimaryHover}`
										}
                  `}
								>
									<Icon icon={tab.icon} size="sm" className="mr-1.5" />
									<span
										className={`text-sm ${activeTab === tab.id ? 'font-semibold' : 'font-medium'}`}
									>
										{tab.label}
									</span>
								</button>
							))}
						</div>

						{/* Tab Content
						    ColumnDetailsTab doesn't have its own isReadOnly handling
						    yet, so we wrap it in pointer-events-none/opacity-60 to
						    preserve the existing "look, don't touch" behavior on
						    read-only lists. FieldsTab knows about isReadOnly itself
						    — it keeps the field rows clickable so users can inspect
						    the configurator, but disables the configurator inputs. */}
						<div className="flex-1 overflow-hidden flex flex-col">
							{activeTab === 'details' && (
								<div
									className={`flex-1 overflow-hidden flex flex-col ${isReadOnly ? 'pointer-events-none opacity-60' : ''}`}
								>
									<ColumnDetailsTab
										selectedColumn={selectedColumn}
										selectedColumnIndex={selectedColumnIndex}
										listConfig={listConfig}
										configuration={configuration}
										availableFields={availableFields}
										onUpdateColumn={updateColumn}
										onUpdateList={onUpdateList}
									/>
								</div>
							)}

							{activeTab === 'fields' && (
								<FieldsTab
									selectedColumn={selectedColumn}
									configuration={configuration}
									onUpdateColumn={updateColumn}
									isModalOpen={isModalOpen}
									allColumns={columns}
									onUpdateAllColumns={(updatedColumns) => {
										onUpdateList({ columns: updatedColumns });
									}}
									isReadOnly={isReadOnly}
								/>
							)}
						</div>
					</>
				)}

				{!selectedColumn && (
					<div className="flex-1 flex items-center justify-center p-6">
						<p
							className={`text-base ${colors.classes.textSecondary} text-center`}
						>
							Select a column to configure
						</p>
					</div>
				)}
			</div>
		</div>
	);
}
